import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, DatePicker, Dialog, Dropdown, GenericTable, Input, Loader, tCell } from "../utils";
import { useDebounce,useDevices, useGraphTimelineData, useTelemetryData, useUserDetail } from "../../services/hooks";
import { buttonClasses } from "../utils/theme";
import DateRangePicker from "../utils/GenericDateRangeFilter/GenericDateRangeFilter";
import DeviceTimelineGraph from "./DeviceTimelineGraph";
import FilePreview from "../utils/FilePreview";
import { isFilePath } from "../../services/functions";
import no_data_found from "../../Images/no_data_found.svg";

const HEARTBEAT = "HEARTBEAT";
const JB_OPEN = "JbOpen";
const BOOM_OPEN = "BoomOpen";
const TAG_SCAN = "TAG-SCAN";
const VEHICLE_CAPTURE = "VEHICLE-CAPTURE";
const DEVICE_TIMELINE_VERSION = "2.0";
const MAX_GRAPH_RANGE_DAYS = 7;
const DEFAULT_GRAPH_FILTER = () => ({
	startDay: dayjs(),
	endDay: dayjs(),
});

const getDefaultTelemetryType = (deviceType) => (deviceType == "all" ? TAG_SCAN : HEARTBEAT);
// The UI works with local calendar-day picks, but the timeline API filters by UTC `day`.
// A single local day can span two UTC dates, so we derive UTC day strings from the local
// start/end boundaries instead of sending the picked local date verbatim.
// The picker works in local calendar days, but the backend filters timeline documents by UTC day.
// Converting the selected local boundary to an ISO date string intentionally expands a local day
// into the UTC day range that actually covers that local window.
const formatUtcRequestDay = (value, boundary = "start") => {
	const date = boundary === "end" ? dayjs(value).endOf("day") : dayjs(value).startOf("day");
	return date.toDate().toISOString().slice(0, 10);
};

const normalizeGraphRange = (startDay, endDay, changedField) => {
	const today = dayjs().startOf("day");
	let nextStart = dayjs(startDay).startOf("day");
	let nextEnd = dayjs(endDay).startOf("day");

	if (!nextStart.isValid()) nextStart = today;
	if (!nextEnd.isValid()) nextEnd = today;

	if (nextStart.isAfter(today)) nextStart = today;
	if (nextEnd.isAfter(today)) nextEnd = today;

	if (nextStart.isAfter(nextEnd)) {
		if (changedField === "startDay") nextEnd = nextStart;
		else nextStart = nextEnd;
	}

	if (nextEnd.diff(nextStart, "day") > MAX_GRAPH_RANGE_DAYS - 1) {
		if (changedField === "startDay") {
			nextEnd = nextStart.add(MAX_GRAPH_RANGE_DAYS - 1, "day");
			if (nextEnd.isAfter(today)) nextEnd = today;
		} else {
			nextStart = nextEnd.subtract(MAX_GRAPH_RANGE_DAYS - 1, "day");
		}
	}

	return { startDay: nextStart, endDay: nextEnd };
};

const ViewTelemetry = ({ open, onClose, title, deviceData }) => {
	const isGlobalTelemetry = deviceData?.deviceType == "all";
	let _defaultFilter = {
		type: getDefaultTelemetryType(deviceData?.deviceType),
		page: 1,
		limit: 100,
		deviceId: deviceData?.imei,
		plantId: deviceData?.plantId,
		startTime: dayjs().subtract(1, "hour").toDate(),
		endTime: new Date(),
	};

	const { userDetail } = useUserDetail();
	const [filters, setFilters] = useState(_defaultFilter);
	const { page, limit } = filters;
	const [isGraph, setIsGraph] = useState(false);
	const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
	const [imagePreviewData, setImagePreviewData] = useState(null);
	const [graphFilter, setGraphFilter] = useState(DEFAULT_GRAPH_FILTER);
	const { devices: plantDevices = [] } = useDevices(
		isGlobalTelemetry && deviceData?.plantId
			? {
					plantIds: deviceData.plantId,
					page_no: 1,
					pagesize: 100,
			  }
			: null
	);
	const deviceNameMap = Object.fromEntries(
		(plantDevices || []).map((device) => [device?.imei, device?.Displayname || device?.displayName || ""])
	);

	const { telemetryData, isLoading, count, getTelemetryData } = useTelemetryData({
		..._defaultFilter,
		startTime: _defaultFilter.startTime.toISOString(),
		endTime: _defaultFilter.endTime.toISOString(),
	});
	// const isCwcUser = userDetail?.email === "cwc_support@lynkit.in";
	const showTimelineToggle =  !isGlobalTelemetry;
	// console.log("showTimelineToggle", showTimelineToggle, !isGlobalTelemetry )
	const { graphTimelineData, isLoading: isGraphTImeLineLoading, getGraphTimelineData } = useGraphTimelineData(null);
	let typeOpts = [{ label: "HEARTBEAT ", value: HEARTBEAT }];
	if (deviceData?.deviceType == "all") {
		typeOpts = [
			{ label: "Tag Scan", value: TAG_SCAN },
			{ label: "Vehicle Capture", value: VEHICLE_CAPTURE },
		];
	} else if (deviceData.deviceType == "rfidReader") {
		typeOpts.push({ label: "JbOpen", value: JB_OPEN }, { label: "BoomOpen", value: BOOM_OPEN }, { label: "Tag Scan", value: TAG_SCAN });
	} else if (deviceData.deviceType == "camera") {
		typeOpts.push({ label: "Vehicle Capture", value: VEHICLE_CAPTURE });
	}

	const typeValue = typeOpts.find((_) => _.value == filters?.type) || null;

	let header = ["S.no"];
	if (isGlobalTelemetry) {
		header.push("Device");
	}
	header.push("Packet Time", "Type");
	if (typeValue?.value == TAG_SCAN) {
		header.push("Antenna ID", "TID", "VMS Response");
	} else if (typeValue?.value == VEHICLE_CAPTURE) {
		header.push("Vehicle No", "VMS Response");
	}

	const handleVehiclePreview = (record) => {
		setImagePreviewOpen(true);
		setImagePreviewData(record);
	};

	const handleCloseImagePreview = () => {
		setImagePreviewOpen(false);
		setImagePreviewData(null);
	};

	const rows = telemetryData?.map((d, i) => {
		let row = [];
		row.push(tCell((page - 1) * limit + i + 1));
		if (isGlobalTelemetry) {
			const currentDeviceId = d?.metadata?.deviceId || "-";
			const currentDeviceName = deviceNameMap?.[currentDeviceId];
			row.push(
				tCell(
					<div>
						<div style={{ fontWeight: 600, color: "#171717" }}>{currentDeviceId}</div>
						{currentDeviceName ? <div style={{ fontSize: "12px", color: "#6b7280" }}>{currentDeviceName}</div> : null}
					</div>
				)
			);
		}
		row.push(tCell(dayjs(d?.timestamp).format("DD MMM YYYY, hh:mm:ss A")), tCell(d?.type));
		if (typeValue?.value == TAG_SCAN) {
			row.push(tCell(d.antennaId), tCell(d.tId), tCell(d.vmsResponse));
		} else if (typeValue?.value == VEHICLE_CAPTURE) {
			row.push(
				tCell(d.vehicleNumber, d.anprImagePath ? "#FF7200" : "", d.anprImagePath ? "pointer" : "", () => handleVehiclePreview(d), i, "vehicleNumber"),
				tCell(d.vmsResponse), 
				// tCell(d.anprImagePath || "-")
			);
		}
		return row;
	});

	const onPageChange = (page) => {
		setFilters((o) => ({ ...o, page: page + 1 }));
		getTelemetryData({ ...filters, page: page + 1, startTime: filters.startTime.toISOString(), endTime: filters.endTime.toISOString() });
	};
	const onPageSizeChange = (e) => {
		setFilters((o) => ({ ...o, limit: e.target.value }));
		getTelemetryData({ ...filters, limit: e.target.value, startTime: filters.startTime.toISOString(), endTime: filters.endTime.toISOString() });
	};

	const debouncedRequest = useDebounce(() => {
		getTelemetryData({ ...filters, startTime: filters.startTime.toISOString(), endTime: filters.endTime.toISOString() });
	});

	const handleFilterChange = (e) => {
		let { name, value } = e.target;
		if (name == "type" && !value && isGlobalTelemetry) {
			value = TAG_SCAN;
		}
		setFilters((old) => ({ ...old, [name]: value }));
		if (name == "tId" && value != "" && value.length < 24) return;
		debouncedRequest();
	};
	const handleResetFilters = () => {
		setFilters(_defaultFilter);
		getTelemetryData({ ..._defaultFilter, startTime: _defaultFilter.startTime.toISOString(), endTime: _defaultFilter.endTime.toISOString() });
	};

	const handleDateRangeSelect = (range) => {
		if (range) {
			let _start = new Date(range.startDate);
			let _end = new Date(range.endDate);
			setFilters((old) => ({ ...old, startTime: _start, endTime: _end }));

			getTelemetryData({ ...filters, startTime: _start.toISOString(), endTime: _end.toISOString() });
		}
	};
	useEffect(() => {
		if (!isGraph || !deviceData?.plantId || !deviceData?.imei) return;
		fetchGraphData();
	}, [isGraph, deviceData?.plantId, deviceData?.imei, graphFilter.startDay, graphFilter.endDay]);
	const handleGraphFilterChange = (e) => {
		const { name, value } = e.target;
		setGraphFilter((prev) => normalizeGraphRange(
			name === "startDay" ? value : prev.startDay,
			name === "endDay" ? value : prev.endDay,
			name
		));
	};

	const handleGraphResetFilters = () => {
		setGraphFilter(DEFAULT_GRAPH_FILTER());
	};
	const fetchGraphData = async () => {
			const payload = {
				plantId: deviceData?.plantId,
				deviceId: deviceData?.imei,
				startDay: formatUtcRequestDay(graphFilter.startDay, "start"),
				endDay: formatUtcRequestDay(graphFilter.endDay, "end"),
				version: DEVICE_TIMELINE_VERSION,
			};
			await getGraphTimelineData(payload);
	};
	const timelineRecords = graphTimelineData?.[0]?.records || [];
	return (
		<Dialog size="xl" open={open} handleClose={onClose} noBtn minHeight={showTimelineToggle && isGraph ? "auto" : "80vh"}
			title={
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "90%" }}>
					<span>{title}</span>

					{/* Segmented Control */}
					{showTimelineToggle && <div style={{
						display: "flex",
						alignItems: "center",
						background: "#e9ecef",
						borderRadius: "8px",
						padding: "3px",
						cursor: "pointer",
					}}>
						<div
							onClick={() => setIsGraph(false)}
							style={{
								padding: "5px 16px",
								borderRadius: "6px",
								fontSize: "12px",
								fontWeight: 600,
								color: !isGraph ? "#fff" : "#555",
								background: !isGraph ? "#FF7200" : "transparent",
								boxShadow: !isGraph ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
								transition: "all 0.25s ease",
								userSelect: "none",
							}}
						>
							Packets
						</div>
						<div
							onClick={() => setIsGraph(true)}
							style={{
								padding: "5px 16px",
								borderRadius: "6px",
								fontSize: "12px",
								fontWeight: 600,
								color: isGraph ? "#fff" : "#555",
								background: isGraph ? "#FF7200" : "transparent",
								boxShadow: isGraph ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
								transition: "all 0.25s ease",
								userSelect: "none",
							}}
						>
							Timeline
						</div>
					</div>}
				</div>
			}
			>
			{showTimelineToggle && isGraph?
			<>
				<div style={{ display: "flex", flexWrap: "wrap", marginBottom: "5px" }}>
					<div style={{ maxWidth: "180px", minWidth: "180px"}}>
						<DatePicker
							label="Start Date"
							name="startDay"
							value={graphFilter.startDay}
							maxDATE={dayjs(graphFilter.endDay)}
							onChange={(value) => handleGraphFilterChange({ target: { name: "startDay", value } })}
							isRequired
						/>
					</div>
					<div style={{ maxWidth: "180px", minWidth: "180px", margin: "0px 5px" }}>
						<DatePicker
							label="End Date"
							name="endDay"
							value={graphFilter.endDay}
							minDATE={dayjs(graphFilter.startDay)}
							maxDATE={dayjs()}
							onChange={(value) => handleGraphFilterChange({ target: { name: "endDay", value } })}
							isRequired
						/>
					</div>
					<div style={{ minWidth: "180px", display: "flex", gap: "0.5rem", maxHeight: "37px" }}>
						<Button text="Clear" onClick={handleGraphResetFilters} style={buttonClasses.lynkitOrangeEmpty} />
					</div>
				</div>
				{isGraphTImeLineLoading ? (
					<div style={{ textAlign: "center", padding: "40px" }}>
						<Loader/>
					</div>
					) : (
					<div
						style={{
							borderRadius: "20px",
							padding: "18px",
							boxShadow: "0 18px 42px rgba(34, 34, 34, 0.12)",
							background: "linear-gradient(180deg, #ffffff 0%, #fbfbfb 100%)",
						}}
					>
						<div style={{ marginBottom: "14px" }}>
							<div style={{ fontSize: "18px", fontWeight: 700, color: "#171717" }}>
								Device Timeline
							</div>
						</div>
						<DeviceTimelineGraph data={timelineRecords} startDay={graphFilter.startDay} endDay={graphFilter.endDay} />
					</div>
					)}
				</>
			:
			<>
			<div style={{ display: "flex", flexWrap: "wrap", marginBottom: "5px" }}>
				<div style={{ maxWidth: "275px", minWidth: "265px" }}>
					<DateRangePicker onDateRangeSelect={handleDateRangeSelect} initialStartDate={filters.startTime} initialEndDate={filters.endTime} isHourTime={true} />
				</div>
				<div style={{ maxWidth: "180px", minWidth: "180px", margin: "0px 5px" }}>
					<Dropdown
						id="type"
						name="type"
						label="Type"
						onChange={(op) => handleFilterChange({ target: { value: op?.value || (isGlobalTelemetry ? TAG_SCAN : ""), name: "type" } })}
						options={typeOpts}
						value={typeValue}
					/>
				</div>
				{typeValue?.value == TAG_SCAN ? (
					<div style={{ maxWidth: "180px", minWidth: "180px", margin: "0px 5px" }}>
						<Input id="tId" name="tId" label="TID" onChange={handleFilterChange} value={filters.tId} />
					</div>
				) : typeValue?.value == VEHICLE_CAPTURE ? (
					<div style={{ maxWidth: "180px", minWidth: "180px", margin: "0px 5px" }}>
						<Input id="vNo" name="vehicleNumber" label="Vehicle No" onChange={handleFilterChange} value={filters.vehicleNumber} />
					</div>
				) : null}

				<div style={{ minWidth: "180px", display: "flex", gap: "0.5rem", maxHeight: "37px" }}>
					<Button text="Clear" onClick={handleResetFilters} style={buttonClasses.lynkitOrangeEmpty} />
				</div>
			</div>

			<GenericTable header={header} rows={rows} pageCount={count} pageNo={page} limit={limit} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} isLoading={isLoading} />
			<Dialog
				size="md"
				open={imagePreviewOpen}
				handleClose={handleCloseImagePreview}
				noBtn
				title={imagePreviewData ? `ANPR Preview - ${imagePreviewData.vehicleNumber || "-"}` : "ANPR Preview"}
				minHeight="60vh"
			>
				{imagePreviewData?.anprImagePath && isFilePath(imagePreviewData.anprImagePath) ? (
					<div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
						<FilePreview
							fileKey={imagePreviewData.anprImagePath}
							extension={String(imagePreviewData.anprImagePath).split(".").pop()}
							inline
							fallbackText="No image preview"
						/>
					</div>
				) : (
					<div style={{ minHeight: "50vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", textAlign: "center" }}>
						<img src={no_data_found} alt="No image preview" style={{ width: "240px", maxWidth: "100%" }} />
						<div style={{ color: "var(--text-color)", fontWeight: 500 }}>No image preview</div>
					</div>
				)}
			</Dialog>
		</>
		}		
		</Dialog>
	);
};

export default ViewTelemetry;
